'use client';

import Link from 'next/link';
import { ExternalLink, Check } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { useScrollReveal } from '@/app/hooks/use-scroll-reveal';
import { VideoPlayer } from '@/app/components/ui/video-player';

const FEATURES = [
  'Manage bookings end-to-end',
  'Member self-service portal',
  'Stripe payments built in',
  'Real-time availability',
] as const;

function FeatureCheckItem({
  label,
  delay,
  isVisible,
  reducedMotion,
}: {
  label: string;
  delay: number;
  isVisible: boolean;
  reducedMotion: boolean | null;
}) {
  return (
    <motion.li
      initial={{ opacity: 0, x: reducedMotion ? 0 : -8 }}
      animate={isVisible ? { opacity: 1, x: 0 } : { opacity: 0 }}
      transition={{
        duration: reducedMotion ? 0.15 : 0.5,
        delay,
        ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
      }}
      className="flex items-center gap-3"
    >
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-[rgba(31,107,68,0.3)] bg-[rgba(31,107,68,0.18)]">
        <Check size={14} strokeWidth={3} className="text-[#7BD2A2]" />
      </span>
      <span className="text-base font-medium text-sandron-ink-100">
        {label}
      </span>
    </motion.li>
  );
}

export function FeaturedSpaceNest() {
  const { ref, isVisible } = useScrollReveal();
  const prefersReducedMotion = useReducedMotion();

  const fadeUp = (delay: number, y = 20) => ({
    initial: { opacity: 0, y: prefersReducedMotion ? 0 : y },
    animate: isVisible ? { opacity: 1, y: 0 } : { opacity: 0 },
    transition: {
      duration: prefersReducedMotion ? 0.15 : 0.65,
      delay: delay / 1000,
      ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
    },
  });

  return (
    <section className="relative overflow-hidden bg-sandron-ink-900 py-[clamp(96px,12vw,160px)]">
      {/* Decorative green glow */}
      <div
        className="pointer-events-none absolute -right-[200px] -top-[200px] z-0 h-[800px] w-[800px] rounded-full blur-[80px] motion-safe:animate-glow-drift"
        style={{
          background:
            'radial-gradient(circle, rgba(31,107,68,0.35) 0%, rgba(31,107,68,0.12) 35%, transparent 70%)',
        }}
        aria-hidden="true"
      />
      {/* Secondary glow */}
      <div
        className="pointer-events-none absolute -bottom-[150px] -left-[150px] z-0 h-[500px] w-[500px] rounded-full opacity-40 blur-[80px]"
        style={{
          background:
            'radial-gradient(circle, rgba(31,107,68,0.3) 0%, transparent 70%)',
        }}
        aria-hidden="true"
      />

      <div
        ref={ref}
        className="relative z-10 mx-auto grid max-w-[1200px] items-center gap-12 px-5 md:px-10 lg:grid-cols-[1fr_1.05fr] lg:gap-20 lg:px-0"
      >
        {/* Text column */}
        <div>
          {/* Product badge */}
          <motion.div
            {...fadeUp(0, 12)}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.06] px-3.5 py-1.5"
          >
            <span className="flex h-6 w-6 items-center justify-center rounded bg-spacenest-primary text-xs font-bold text-white">
              S
            </span>
            <span className="text-sm font-medium text-sandron-ink-100">
              SpaceNest
            </span>
          </motion.div>

          {/* Eyebrow */}
          <motion.p
            {...fadeUp(60, 8)}
            className="mt-5 text-xs font-semibold uppercase tracking-[0.12em] text-[#7BD2A2]"
          >
            Featured product
          </motion.p>

          {/* Headline */}
          <motion.h2
            {...fadeUp(150, 24)}
            className="mt-4 font-display text-[clamp(36px,4.5vw,64px)] font-extrabold leading-[1.06] tracking-[-0.025em] text-white"
          >
            Book community spaces across Ireland.
          </motion.h2>

          {/* Body */}
          <motion.p
            {...fadeUp(240, 16)}
            className="mt-5 text-[17px] leading-[1.65] text-sandron-ink-300"
          >
            From community centres to schools to council facilities. SpaceNest
            helps organisations open up their spaces — including school
            facilities outside term hours and during holidays — and helps
            people find and book them.
          </motion.p>

          {/* Feature list */}
          <ul className="mt-8 flex flex-col gap-3.5">
            {FEATURES.map((feature, i) => (
              <FeatureCheckItem
                key={feature}
                label={feature}
                delay={0.32 + i * 0.06}
                isVisible={isVisible}
                reducedMotion={prefersReducedMotion}
              />
            ))}
          </ul>

          {/* CTAs */}
          <motion.div
            {...fadeUp(600, 12)}
            className="mt-10 flex flex-wrap gap-3"
          >
            <Link
              href="/products/spacenest"
              className="group inline-flex h-14 items-center gap-2.5 rounded-full bg-white px-7 font-display text-base font-semibold text-sandron-ink-900 transition-all duration-200 hover:-translate-y-px hover:bg-sandron-ink-50"
            >
              Visit SpaceNest
              <ExternalLink
                size={16}
                className="transition-transform duration-200 group-hover:translate-x-0.5"
              />
            </Link>
            <Link
              href="/about"
              className="inline-flex h-14 items-center rounded-full border border-white/20 px-7 font-display text-base font-semibold text-white transition-all duration-200 hover:border-white/40 hover:bg-white/[0.06]"
            >
              Read the case study
            </Link>
          </motion.div>
        </div>

        {/* Member video */}
        <motion.div
          initial={{ opacity: 0, x: prefersReducedMotion ? 0 : 32 }}
          animate={isVisible ? { opacity: 1, x: 0 } : { opacity: 0 }}
          transition={{
            duration: prefersReducedMotion ? 0.15 : 0.7,
            delay: 0.2,
            ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
          }}
        >
          <VideoPlayer
            src="/videos/spacenest-member.mp4"
            title="SpaceNest member onboarding — discover, book, pay"
            caption="Discover a centre, pick a slot, pay — in under a minute."
            variant="dark"
            aspectClass="aspect-video"
          />
        </motion.div>
      </div>
    </section>
  );
}
